import type { ReactNode } from 'react';
import { UploadEmptyState } from './UploadEmptyState';
import { CollapsibleFieldExplainer } from './CollapsibleFieldExplainer';
import type { ExplainerSection } from './FieldExplainer';

interface DraftSurfaceProps {
  isEmpty: boolean;
  /** i18n key of the "what we need per row" heading */
  headingKey: string;
  sections: ExplainerSection[];
  downloadTemplate: () => void;
  /** Parses the file into the step's draft state; returns the parsed row count */
  parse: (file: File) => Promise<number>;
  onAddManually: () => void;
  /** i18n key of the manual-add action, named after the entity */
  addLabelKey: string;
  /** Further import buttons under the dropzone (forwarded to UploadEmptyState) */
  importActions?: ReactNode;
  /** Replaces the explainer empty state */
  emptyState?: ReactNode;
  /** Rendered under the empty state only (e.g. seeded entities that survive it) */
  emptyExtras?: ReactNode;
  /** The step's table(s), shown once rows exist */
  children: ReactNode;
}

/**
 * The empty ⇆ filled branch every draft step shares: the explainer empty
 * state (plus optional extras) until content exists, then the collapsible
 * explainer above the step's tables.
 */
export function DraftSurface({
  isEmpty,
  headingKey,
  sections,
  downloadTemplate,
  parse,
  onAddManually,
  addLabelKey,
  importActions,
  emptyState,
  emptyExtras,
  children
}: DraftSurfaceProps) {
  if (isEmpty) {
    return (
      <>
        {emptyState ?? (
          <UploadEmptyState
            headingKey={headingKey}
            sections={sections}
            downloadTemplate={downloadTemplate}
            parse={parse}
            onAddManually={onAddManually}
            addLabelKey={addLabelKey}
            importActions={importActions}
          />
        )}
        {emptyExtras}
      </>
    );
  }
  return (
    <div className="space-y-6">
      <CollapsibleFieldExplainer headingKey={headingKey} sections={sections} />
      {children}
    </div>
  );
}
